import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config';
import { categories } from '../constants/floraFaunaData';

const FamilyDetails = () => {
    const { familyId } = useParams();
    const [family, setFamily] = useState(null);
    const [genera, setGenera] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Family
                const familyResponse = await fetch(`${API_BASE_URL}/families/read.php`);
                const familyData = await familyResponse.json();
                const foundFamily = familyData.records?.find(f => f.id == familyId);

                if (foundFamily) {
                    setFamily(foundFamily);

                    // Fetch Genera
                    const generaResponse = await fetch(`${API_BASE_URL}/genera/read.php`);
                    const generaData = await generaResponse.json();
                    if (generaData.records) {
                        setGenera(generaData.records.filter(g => g.family_id == familyId));
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [familyId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    if (!family) {
        return <div className="p-20 text-center text-slate-500">Family not found.</div>;
    }

    // Get category metadata for styling
    const categoryMeta = categories.find(c => c.id === family.category_id) || {};

    return (
        <section className="min-h-screen bg-white font-sans selection:bg-emerald-100 selection:text-emerald-900">
            <div className="max-w-7xl mx-auto px-6 pt-32 pb-24">
                {/* Breadcrumb / Back */}
                <Link to={`/flora-fauna/category/${family.category_id}`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-emerald-600 mb-8 transition-colors">
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Back to {categoryMeta.label || 'Collection'}</span>
                </Link>

                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <span className="text-emerald-600 font-bold uppercase tracking-[0.2em] text-xs mb-4 block">
                            Taxonomic Family
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-4 font-display leading-tight">
                            {family.name}
                        </h1>
                        <h2 className="text-2xl font-serif italic text-slate-500 mb-8">
                            {family.common_name}
                        </h2>
                        <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                            {family.description || "No description available for this family."}
                        </p>
                    </motion.div>

                    <div className="relative aspect-video lg:aspect-square bg-slate-100 rounded-3xl overflow-hidden">
                        {family.image_url ? (
                            <img src={family.image_url} alt={family.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center h-full text-slate-300">
                                <FontAwesomeIcon icon={faLeaf} className="text-6xl" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Genera List */}
                <div className="border-t border-slate-100 pt-16">
                    <h3 className="text-2xl font-bold text-slate-900 mb-10 font-display">Accepted Genera</h3>

                    {genera.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {genera.map((genus, i) => (
                                <Link to={`/flora-fauna/genus/${genus.id}`} key={genus.id}>
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: i * 0.05 }}
                                        className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-emerald-100 p-6 rounded-2xl transition-all duration-300 hover:shadow-xl hover:shadow-emerald-900/5"
                                    >
                                        <div className="flex items-center justify-between mb-4">
                                            <span className="w-8 h-8 rounded-full bg-white text-emerald-600 flex items-center justify-center text-xs font-bold border border-slate-100 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                                                {genus.name.charAt(0)}
                                            </span>
                                            <FontAwesomeIcon icon={faLeaf} className="text-slate-300 group-hover:text-emerald-400 transition-colors" />
                                        </div>
                                        <h4 className="text-lg font-bold text-slate-900 group-hover:text-emerald-700 font-display mb-1">{genus.name}</h4>
                                        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 group-hover:text-emerald-500/70">{genus.common_name}</p>
                                    </motion.div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium">No genera recorded for this family yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default FamilyDetails;
